import {
  generators,
  HostStreamArchState,
  PlaycastMessage,
  PlaycastMessageTarget,
  RegistryId,
  StreamComposition,
  StreamFrameOutputEntry,
  StreamId,
  StreamListEntry,
  StreamSinkEntry,
} from '@playcastdotio/Messaging';

type SendFn = (message: PlaycastMessage<PlaycastMessageTarget>) => void;
type ChangeFn = (state: HostStreamArchState) => void;

// Mirrors the C++ StreamDataType bit flags. Only the Frame bit is needed host-side (to tell which
// sinks are cameras); the Messaging barrel re-exports types only, so the value is defined locally.
const STREAM_DATA_TYPE_FRAME = 1 << 0;

/**
 * Renderer-side view of Playjector's stream world.
 *
 * Wraps the `stream.*` generators (enumerate / list / create / remove / modify) and keeps a
 * `streamId -> composition` table plus the registry catalogs (frameOutputs + a single unified
 * `streamSinks` list) in sync from Playjector's replies. Every sink - camera, audio, or HID device -
 * is one `streamSinks` entry carrying its supported/enabled data types, so callers filter by
 * capability (e.g. the Frame bit) instead of by a separate list. Multi-stream capable: any tracked
 * stream can be addressed by `streamId`; the base stream is a single entry identified by
 * `baseStreamId` and is the default target callers use today.
 *
 * `send` and `onChange` are injected so the class stays unit-testable without real IPC. `onChange`
 * receives an immutable snapshot after every state mutation; the owning StreamCoordinator forwards
 * it to its `streamStateChanged` event. This is a faithful port of the former electron-side
 * StreamRegistry — the electron process is now a pipe and this state lives in the renderer.
 */
export class StreamRegistryManager {
  private baseStreamIdValue: StreamId = '';
  private streams: Map<StreamId, StreamListEntry> = new Map();
  private frameOutputs: StreamFrameOutputEntry[] = [];
  private streamSinks: StreamSinkEntry[] = [];

  constructor(
    private readonly send: SendFn,
    private readonly onChange?: ChangeFn,
  ) {}

  // --- Enumeration / discovery senders ---
  enumerateAll(): void {
    this.send(generators.stream.enumerateAll());
  }

  list(): void {
    this.send(generators.stream.list());
  }

  // --- Lifecycle senders ---
  createStream(composition: StreamComposition): void {
    this.send(generators.stream.create(composition));
  }

  removeStream(streamId: StreamId): void {
    this.send(generators.stream.remove(streamId));
  }

  // --- Modify senders (streamId-addressed) ---
  setFrameOutput(streamId: StreamId, frameOutput: RegistryId): void {
    this.send(generators.stream.setFrameOutput(streamId, frameOutput));
  }

  addSink(streamId: StreamId, sink: RegistryId): void {
    this.send(generators.stream.addSink(streamId, sink));
  }

  removeSink(streamId: StreamId, sink: RegistryId): void {
    this.send(generators.stream.removeSink(streamId, sink));
  }

  // Per-type mute for a sink (sink-level, not stream-scoped). Does NOT change what the sink can
  // carry - only whether an already-supported type is delivered. Only meaningful for a multi-type
  // sink (e.g. mute an A/V camera's audio while it keeps sending video); for a single-type sink,
  // prefer add/removeSink to control participation.
  setSinkTypeEnabled(registryId: RegistryId, type: number, enabled: boolean): void {
    this.send(generators.stream.setSinkTypeEnabled(registryId, type, enabled));
  }

  // --- Base-stream convenience (default target) ---
  setBaseFrameOutput(frameOutput: RegistryId): boolean {
    const id = this.baseStreamIdValue;
    if (!id) return false;
    this.setFrameOutput(id, frameOutput);
    return true;
  }

  /**
   * Add `frameSink` to the base stream, optionally removing exactly one previously-bound sink first.
   * This is a mechanism-only primitive: the caller (StreamCoordinator) decides removal policy - which
   * sink (if any) is the swappable preview camera vs. a pinned peer or the desktop-hosting camera. We
   * intentionally do NOT blanket-remove every frame-capable sink here, so pinned peer cameras and the
   * hosting camera are never detached by a preview swap.
   *
   * `sinkToRemove` is removed only when it is set and differs from `frameSink`.
   */
  setBaseFrameSink(frameSink: RegistryId, sinkToRemove?: RegistryId): boolean {
    const id = this.baseStreamIdValue;
    if (!id) return false;

    if (sinkToRemove !== undefined && sinkToRemove !== frameSink) {
      this.removeSink(id, sinkToRemove);
    }

    this.addSink(id, frameSink);
    return true;
  }

  // --- Resolvers ---
  baseStreamId(): StreamId {
    return this.baseStreamIdValue;
  }

  getFrameOutputs(): StreamFrameOutputEntry[] {
    return this.frameOutputs;
  }

  /**
   * Resolve a host display id to a frame-output RegistryId. A DisplayGrabber's `sourceId` IS its
   * monitor_index (see DXGICaptureBackend::CreateSourceProperties), and the host picker keys on
   * that same `sourceId`, so matching on it is unambiguous.
   */
  resolveFrameOutputForDisplay(displayId: number): RegistryId | undefined {
    return this.frameOutputs.find((fo) => fo.sourceId === displayId)?.registryId;
  }

  /**
   * Resolve a frame sink (e.g. a ShadowCam) to its RegistryId by registered name. Only frame-capable
   * sinks are considered, so a name collision with a non-camera sink can never return the wrong id.
   */
  resolveFrameSinkByName(name: string): RegistryId | undefined {
    return this.streamSinks.find((s) => s.name === name && this.entryIsFrameCapable(s))?.registryId;
  }

  getState(): HostStreamArchState {
    const streams: Record<StreamId, StreamListEntry> = {};
    for (const [id, entry] of this.streams) streams[id] = entry;
    return {
      baseStreamId: this.baseStreamIdValue,
      streams,
      frameOutputs: [...this.frameOutputs],
      streamSinks: [...this.streamSinks],
    };
  }

  private entryIsFrameCapable(entry: StreamSinkEntry): boolean {
    return (entry.supportedTypes & STREAM_DATA_TYPE_FRAME) !== 0;
  }

  // --- Inbound reply routing. Returns true if the message was a stream reply we consumed. ---
  handleReply(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    if (message.header.target !== 'stream' || !message.header.isReply) return false;

    const body = (message.body?.message ?? {}) as Record<string, unknown>;
    switch (message.header.action) {
      case 'enumerateAll':
        this.applyCatalogs(body);
        break;
      case 'getFrameOutputs':
        if (Array.isArray(body.frameOutputs)) this.frameOutputs = body.frameOutputs as StreamFrameOutputEntry[];
        break;
      case 'getStreamSinks':
        if (Array.isArray(body.streamSinks)) this.streamSinks = body.streamSinks as StreamSinkEntry[];
        break;
      case 'list':
        this.applyStreamList(body);
        break;
      case 'create':
        this.applyCreate(body);
        break;
      case 'remove':
        this.applyRemove(body);
        break;
      case 'setFrameOutput':
      case 'addSink':
      case 'removeSink':
      case 'setSinkTypeEnabled':
        // Composition/enable confirmed on the Playjector side; re-list to pull the authoritative
        // table. Deliberately does NOT emit here — the follow-up `list` reply carries the new state.
        this.list();
        return true;
      case 'attachParticipant':
      case 'detachParticipant':
        // Participant bookkeeping is Playjector-side; participantCount in list is still a stub.
        return true;
      default:
        return false;
    }

    this.emit();
    return true;
  }

  private applyCatalogs(body: Record<string, unknown>): void {
    if (Array.isArray(body.frameOutputs)) this.frameOutputs = body.frameOutputs as StreamFrameOutputEntry[];
    if (Array.isArray(body.streamSinks)) this.streamSinks = body.streamSinks as StreamSinkEntry[];
  }

  private applyStreamList(body: Record<string, unknown>): void {
    if (!Array.isArray(body.streams)) return;
    const entries = body.streams as StreamListEntry[];
    this.streams = new Map();
    for (const entry of entries) {
      if (!entry || typeof entry.streamId !== 'string') continue;
      this.streams.set(entry.streamId, entry);
      if (entry.isBase) this.baseStreamIdValue = entry.streamId;
    }
  }

  private applyCreate(body: Record<string, unknown>): void {
    const streamId = typeof body.streamId === 'string' ? body.streamId : '';
    if (!streamId) return;
    if (!this.streams.has(streamId)) {
      this.streams.set(streamId, {
        streamId,
        frameOutput: 0,
        sinks: [],
        participantCount: 0,
        isBase: false,
      });
    }
    // Pull the authoritative composition (and isBase flags) for the newly created stream.
    this.list();
  }

  private applyRemove(body: Record<string, unknown>): void {
    const streamId = typeof body.streamId === 'string' ? body.streamId : '';
    if (streamId && this.streams.delete(streamId) && this.baseStreamIdValue === streamId) {
      this.baseStreamIdValue = '';
    }
  }

  private emit(): void {
    this.onChange?.(this.getState());
  }
}
